import {
  DatabaseAuto, IbulkDelete,
  IbulkDeleteSuccessResponse, Icompany,
  IcompanySubscription,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfairPolicy, IfilterProps, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for fair policy properties with specific generic types
 * @template T - Type for company ID (default: string)
 * @template C - Type for company details (default: Icompany)
 * @template S - Type for company subscription (default: IcompanySubscription)
 */
type TfairPolicyProps = IfairPolicy<string, Icompany, IcompanySubscription>;

/**
 * Represents a fair policy for a company.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link FairPolicy} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const fairPolicy = await FairPolicy.getAll();
 *
 * const fairPolicy = await FairPolicy.filterAll({
 *
 * });
 *
 * const fairPolicy = await FairPolicy.bulkDelete({
 *
 * });
 *
 * const fairPolicy = await FairPolicy.getOne('resourceId');
 *
 * const fairPolicy = await FairPolicy.remove();
 *
 * ```
 */
export class FairPolicy
  extends DatabaseAuto {
  /** Base API endpoint for fair policy operations */
  private static readonly baseUrl = '/fair-policy';

  /** Unique reference ID for the fair policy */
  readonly urId: TfairPolicyProps['urId'];

  /** Company associated with the fair policy */
  readonly company: TfairPolicyProps['company'];

  /** Feature that has exceeded the limit */
  readonly featureExceeded: TfairPolicyProps['featureExceeded'];

  /** Subscription associated with the fair policy */
  readonly subscription: TfairPolicyProps['subscription'];

  /**
   * Constructs an instance of `FairPolicy`.
   *
   * @param {TfairPolicyProps} data - The fairPolicy data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TfairPolicyProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      }
    });

    super(data);

    this.urId = data.urId;
    this.company = data.company;
    this.featureExceeded = data.featureExceeded;
    this.subscription = data.subscription;
  }

  /**
   * Retrieves all fairPolicys with pagination support.
   *
   * @param {number} [offset=0] - The starting point for pagination.
   * @param {number} [limit=20] - The number of items to retrieve.
   * @returns {Promise<{ count: number, dataArr: FairPolicy[] }>} An object containing
   * the total count and an array of `FairPolicy` instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TfairPolicyProps>>(`${FairPolicy.baseUrl}?${query}`);
    const fairPolicys = await lastValueFrom(observer$);

    return {
      count: fairPolicys.count,
      dataArr: fairPolicys.data.map((val) => new FairPolicy(val))
    };
  }

  /**
   * Filters fairPolicys based on the provided filter properties.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving fairPolicys.
   * @returns {Promise<{ count: number, dataArr: FairPolicy[] }>} An object containing
   * the total count and an array of `FairPolicy` instances.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TfairPolicyProps>>(`${FairPolicy.baseUrl}/filter`, filter);
    const fairPolicys = await lastValueFrom(observer$);

    return {
      count: fairPolicys.count,
      dataArr: fairPolicys.data.map((val) => new FairPolicy(val))
    };
  }

  /**
   * Retrieves a single fairPolicy based on the provided resource ID.
   *
   * @param {string} resourceId - The unique identifier for the fairPolicy to retrieve.
   * @returns {Promise<FairPolicy>} The `FairPolicy` instance corresponding to the provided resource ID.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TfairPolicyProps>(`${FairPolicy.baseUrl}/${resourceId}`);
    const fairPolicy = await lastValueFrom(observer$);

    return new FairPolicy(fairPolicy);
  }


  /**
   * Removes multiple fair policies based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk deletion operation
   * @throws {Error} When the deletion operation fails or payload is invalid
   *
   * @example
   * const result = await FairPolicy.bulkDelete({
   *   ids: ['policy1', 'policy2'],
   *   hardDelete: false
   * });
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${FairPolicy.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current fair policy from the database.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} When the deletion operation fails or the policy doesn't exist
   *
   * @example
   * const policy = await FairPolicy.getOne('policy123');
   * const result = await policy.remove();
   * if (result?.success) {
   *   console.log('Policy removed successfully');
   * }
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${FairPolicy.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
